'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { pickPassage } from '@/features/typing-engine';
import { appendAttempt, getPersonalBest } from '@/lib/storage/attempts';
import { submitAttemptAction } from '@/actions/attempts';
import { useTypingTest } from '@/hooks/use-typing-test';
import { DurationPicker } from '@/components/typing/duration-picker';
import { PassageDisplay } from '@/components/typing/passage-display';
import { MetricsBar } from '@/components/typing/metrics-bar';
import { ResultScreen } from '@/components/typing/result-screen';
import { Button } from '@/components/ui/button';
import { ErrorMessage } from '@/components/ui/feedback';

export function TypingTest({ defaultDuration = 60, autoStart = false, isAuthenticated = false }) {
  const [duration, setDuration] = useState(defaultDuration);
  const [passageIndex, setPassageIndex] = useState(0);
  const [saveError, setSaveError] = useState(null);
  const [personalBest, setPersonalBest] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);
  const inputRef = useRef(null);

  const passage = pickPassage(passageIndex, 'easy');
  const test = useTypingTest({ text: passage.text, durationSeconds: duration });
  const { phase, metrics, expectedChars, currentIndex } = test;

  const [typedResults, setTypedResults] = useState([]);
  useEffect(() => {
    const results = new Array(expectedChars.length).fill(null);
    for (let i = 0; i < currentIndex; i++) results[i] = 'correct';
    setTypedResults(results);
  }, [currentIndex, expectedChars.length]);

  useEffect(() => {
    if (phase === 'running') inputRef.current?.focus();
  }, [phase]);

  useEffect(() => {
    setPersonalBest(getPersonalBest('typing_test', duration));
  }, [duration]);

  test.setFinishCallback(async (finalMetrics) => {
    const record = {
      activityType: 'typing_test',
      activityRefSlug: `${duration}s`,
      durationSeconds: Math.max(1, Math.round(finalMetrics.elapsedSeconds)),
      grossWpm: finalMetrics.grossWpm,
      netWpm: finalMetrics.netWpm,
      accuracy: finalMetrics.accuracy,
      correctChars: finalMetrics.correctChars,
      incorrectChars: finalMetrics.incorrectChars,
      totalChars: finalMetrics.totalChars,
      backspaces: finalMetrics.backspaces,
    };

    // Local save always happens (works for anonymous visitors and offline).
    const local = appendAttempt(record);
    if (!local) {
      setSaveError('Your result could not be saved locally. This may be due to private browsing.');
    }
    setPersonalBest(getPersonalBest('typing_test', duration));

    // Server save for authenticated users.
    if (isAuthenticated) {
      const result = await submitAttemptAction(record);
      if (result?.error) {
        setSaveError(`Server save failed: ${result.error}. Your result is still saved locally.`);
      } else {
        setSubmitResult(result);
      }
    }
  });

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Backspace') { e.preventDefault(); test.handleBackspace(); return; }
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      test.handleInput(e.key);
      setTypedResults((prev) => {
        const next = [...prev];
        next[test.currentIndex] = 'correct';
        return next;
      });
    }
  }, [test]);

  const handleRetry = () => { test.restart(); setSaveError(null); setSubmitResult(null); };
  const handleNewTest = () => { setPassageIndex((i) => i + 1); test.restart(); setSaveError(null); setSubmitResult(null); };
  const handleDurationChange = (d) => { if (phase === 'running' || phase === 'paused') return; setDuration(d); test.restart(); };

  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (autoStart && !autoStartedRef.current) {
      autoStartedRef.current = true;
      const t = setTimeout(() => test.start(), 100);
      return () => clearTimeout(t);
    }
  }, [autoStart, test]);

  if (phase === 'finished') {
    return (
      <div className="space-y-4">
        {saveError && <ErrorMessage>{saveError}</ErrorMessage>}
        <ResultScreen
          metrics={metrics}
          durationSeconds={duration}
          personalBest={personalBest}
          onRetry={handleRetry}
          onNewTest={handleNewTest}
        />
        {submitResult?.awardedAchievements?.length > 0 && (
          <div className="rounded-md border border-achievement bg-achievement-surface p-3 text-sm">
            New achievements: {submitResult.awardedAchievements.join(', ')}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <DurationPicker value={duration} onChange={handleDurationChange} disabled={phase === 'running' || phase === 'paused'} />
        {phase === 'idle' && <Button onClick={test.start} size="lg">Start test</Button>}
        {phase === 'running' && <Button onClick={test.finish} variant="secondary" size="sm">Finish early</Button>}
        {phase === 'paused' && <Button onClick={test.resume} size="sm">Resume</Button>}
      </div>
      {test.wasAutoPaused && phase === 'paused' && (
        <div className="rounded-md border border-warning/20 bg-warning-surface p-3 text-sm">
          The test was paused because the tab was hidden. Your time has stopped.
        </div>
      )}
      {saveError && <ErrorMessage>{saveError}</ErrorMessage>}
      <MetricsBar grossWpm={metrics.grossWpm} netWpm={metrics.netWpm} accuracy={metrics.accuracy} remainingSeconds={test.remainingSeconds} progress={metrics.progress} />
      <PassageDisplay chars={expectedChars} currentIndex={currentIndex} typedResults={typedResults} />
      <input ref={inputRef} type="text" className="sr-only" aria-label="Typing input"
        onKeyDown={handleKeyDown} readOnly value="" tabIndex={phase === 'running' ? 0 : -1} />
      {phase === 'idle' && (
        <p className="text-center text-sm text-text-muted">
          Click Start test, then begin typing. The test ends automatically after {duration} seconds.
        </p>
      )}
      {phase === 'paused' && !test.wasAutoPaused && (
        <div className="flex justify-center"><Button onClick={test.resume}>Resume</Button></div>
      )}
    </div>
  );
}